/**
 * Responsive grid of `MediaTile`s with roving-tabindex keyboard navigation
 * (Arrow keys / Home / End move focus, matching the WAI-ARIA grid pattern) —
 * the Tab key only stops once on the whole gallery. Presentation + selection
 * only: the caller owns the item list and what "select" means.
 */
import type { JSX } from 'solid-js';
import { createMemo, createSignal, For, onCleanup } from 'solid-js';
import { nextIndex, prevIndex } from '../focus/focus-trap.ts';
import { cx } from '../internal/cx.ts';
import { ensureStyles } from '../internal/inject-styles.ts';
import { MediaTile, type MediaTileKind } from './MediaTile.tsx';

export interface GalleryItem {
  readonly id: string;
  readonly src?: string;
  readonly alt: string;
  readonly kind?: MediaTileKind;
  readonly loading?: boolean;
}

const STYLE_ID = 'f4-gallery';
const CSS = `
.f4-gallery {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(7rem, 1fr));
  gap: var(--space-3);
  padding: 0;
  margin: 0;
  list-style: none;
}
`;

export interface GalleryProps {
  /** Items to render, in display order. */
  readonly items: readonly GalleryItem[];
  /** Currently selected item id, if any. */
  readonly selectedId?: string;
  /** Emitted with the item id on activation. */
  readonly onSelect: (id: string) => void;
  /** Accessible name for the grid (required). */
  readonly label: string;
  readonly class?: string;
}

export function Gallery(props: GalleryProps): JSX.Element {
  ensureStyles(STYLE_ID, CSS);

  const initialFocus = (): number => {
    const idx = props.items.findIndex((item) => item.id === props.selectedId);
    return idx >= 0 ? idx : 0;
  };
  const [focused, setFocused] = createSignal(initialFocus());
  // Clamp reactively: when `items` shrinks below the stored index, fall back to
  // the last item so the grid never loses its single roving tab stop.
  const focusedIndex = createMemo(() => {
    const max = props.items.length - 1;
    return max < 0 ? 0 : Math.min(focused(), max);
  });
  // Element refs keyed by item id, not array position — reordering `items` must
  // move focus to the intended tile, not whatever now sits at that index.
  const tiles = new Map<string, HTMLButtonElement>();

  const moveTo = (index: number): void => {
    if (index < 0 || index >= props.items.length) return;
    setFocused(index);
    const id = props.items[index]?.id;
    if (id !== undefined) tiles.get(id)?.focus();
  };

  const onKeyDown = (event: KeyboardEvent): void => {
    const count = props.items.length;
    if (count === 0) return;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        moveTo(nextIndex(focusedIndex(), count));
        return;
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        moveTo(prevIndex(focusedIndex(), count));
        return;
      case 'Home':
        event.preventDefault();
        moveTo(0);
        return;
      case 'End':
        event.preventDefault();
        moveTo(count - 1);
        return;
      default:
    }
  };

  return (
    <ul aria-label={props.label} class={cx('f4-gallery', props.class)} onKeyDown={onKeyDown}>
      <For each={props.items}>
        {(item, index) => {
          onCleanup(() => tiles.delete(item.id));
          return (
            <li>
              <MediaTile
                ref={(el) => {
                  tiles.set(item.id, el);
                }}
                src={item.src}
                alt={item.alt}
                kind={item.kind}
                loading={item.loading}
                selected={item.id === props.selectedId}
                tabIndex={index() === focusedIndex() ? 0 : -1}
                onSelect={() => {
                  setFocused(index());
                  props.onSelect(item.id);
                }}
              />
            </li>
          );
        }}
      </For>
    </ul>
  );
}
